import { Button, Chip } from '@atlas/ui-primitives'
import { useState } from 'react'
import { PromptModelConfigEditor } from '@/components/prompts/PromptModelConfigEditor'
import { PromptOutputEditor } from '@/components/prompts/PromptOutputEditor'
import type { Prompt, UpdatePromptInput } from '@/domain/prompt.types'

export interface PromptSettingsPanelProps {
	prompt: Prompt
	/** The tenant default for this prompt's type — what applies when nothing is overridden. */
	inherited?: Record<string, unknown>
	onSuggestSchema: () => void
	isSuggesting?: boolean
	suggestRequested?: boolean
	/**
	 * Persist an override. Absent leaves the panel READ-ONLY, which is what the
	 * versions tab wants — a frozen version's config is a record, not a form.
	 */
	onSave?: (patch: UpdatePromptInput) => void
	isSaving?: boolean
}

/** Fields shown in the effective-config summary, in the order an author reads them. */
const CONFIG_FIELDS = ['provider', 'model', 'temperature', 'maxTokens'] as const

/**
 * Model config and output schema, COLLAPSED by default.
 *
 * Deliberately out of the main authoring flow: the whole point of tenant defaults is
 * that an author writes a body, adds variables and publishes without opening this.
 * Values are labelled as inherited so it is obvious nothing was set on the prompt.
 */
export function PromptSettingsPanel({
	prompt,
	inherited = {},
	onSuggestSchema,
	isSuggesting = false,
	suggestRequested = false,
	onSave,
	isSaving = false,
}: PromptSettingsPanelProps) {
	const [open, setOpen] = useState(false)
	const [editing, setEditing] = useState<'config' | 'output' | null>(null)
	const overrides = (prompt.modelConfig ?? {}) as Record<string, unknown>

	return (
		<section aria-label="Settings" className="rounded-ds-card border border-ds-border bg-ds-surface-elevated">
			<button
				type="button"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}
				className="flex w-full items-center justify-between p-4 text-left"
			>
				<span className="text-ds-sm font-medium text-ds-text">Model config &amp; output schema</span>
				<span className="text-ds-sm text-ds-text-subtle">{open ? 'Hide' : 'Show'}</span>
			</button>

			{open ? (
				<div className="border-t border-ds-border p-4">
					<div className="mb-2 flex items-center justify-between">
						<h4 className="text-ds-2xs uppercase tracking-wide text-ds-text-subtle">Effective config</h4>
						{onSave !== undefined && editing === null ? (
							<Button size="sm" variant="muted" onPress={() => setEditing('config')}>
								Edit
							</Button>
						) : null}
					</div>

					{editing === 'config' ? (
						<PromptModelConfigEditor
							config={prompt.modelConfig}
							inherited={inherited}
							isSaving={isSaving}
							onSave={(modelConfig) => {
								onSave?.({ modelConfig })
								setEditing(null)
							}}
							onCancel={() => setEditing(null)}
						/>
					) : (
						<ConfigSummary overrides={overrides} inherited={inherited} />
					)}

					<div className="mb-2 mt-5 flex items-center justify-between">
						<h4 className="text-ds-2xs uppercase tracking-wide text-ds-text-subtle">Output</h4>
						{onSave !== undefined && editing === null ? (
							<Button size="sm" variant="muted" onPress={() => setEditing('output')}>
								Edit
							</Button>
						) : null}
					</div>
					{editing === 'output' ? (
						<PromptOutputEditor
							schema={prompt.outputSchema}
							isSaving={isSaving}
							onSave={(outputSchema) => {
								onSave?.({ outputSchema })
								setEditing(null)
							}}
							onCancel={() => setEditing(null)}
						/>
					) : prompt.outputSchema ? (
						<pre className="whitespace-pre-wrap rounded-ds-md border border-ds-border bg-ds-surface p-3 font-mono text-ds-sm text-ds-text">
							{JSON.stringify(prompt.outputSchema, null, 2)}
						</pre>
					) : (
						<p className="text-ds-sm text-ds-text-muted">
							Plain text — this prompt asks for prose rather than structured data.
						</p>
					)}

					<Button className="mt-3" size="sm" variant="secondary" isDisabled={isSuggesting} onPress={onSuggestSchema}>
						{isSuggesting ? 'Requesting…' : 'Suggest with AI'}
					</Button>

					{suggestRequested ? (
						<p className="mt-2 text-ds-sm text-ds-text-muted">
							Drafting requested. A proposal arrives for review — nothing is applied automatically.
						</p>
					) : null}
				</div>
			) : null}
		</section>
	)
}

/**
 * The effective config, read-only, with each value labelled by where it came from.
 *
 * Split from the panel because it is the READING view while the panel owns the
 * shell and the edit toggle — and together they crossed the size ceiling.
 */
function ConfigSummary({
	overrides,
	inherited,
}: {
	overrides: Record<string, unknown>
	inherited: Record<string, unknown>
}) {
	return (
		<dl className="grid gap-2 text-ds-sm sm:grid-cols-2">
			{CONFIG_FIELDS.map((field) => {
				const isOverridden = overrides[field] !== undefined
				const value = isOverridden ? overrides[field] : inherited[field]

				return (
					<div key={field}>
						<dt className="flex items-center gap-1.5 text-ds-2xs text-ds-text-subtle">
							{field}
							{/* Labelled explicitly: an inherited value must not look like one
										    someone chose for this prompt. */}
							<Chip variant="muted" size="sm">
								{isOverridden ? 'override' : 'inherited'}
							</Chip>
						</dt>
						<dd className="font-mono text-ds-text">{value === undefined ? '—' : String(value)}</dd>
					</div>
				)
			})}
		</dl>
	)
}
